import { type CSSProperties, type ReactNode } from "react";

import { Box } from "@mui/material";

import { mediaPerspective } from "@/src/components/atoms/VehicleSilhouette/VehicleSilhouette.lib";

import { VehicleType } from "@/src/types/types";

interface VehicleSilhouetteProps {
  silhouette: string;
  vehicleType: VehicleType;
  bikeTypeId?: number;
}

interface ImageContainerProps {
  children: ReactNode;
}

interface SilhouetteImageProps {
  silhouette: string;
  vehicleType: VehicleType;
  bikeTypeId?: number;
  altText: string;
  style?: CSSProperties;
}

const ImageContainer = ({ children }: ImageContainerProps) => (
  <Box
    sx={{
      width: "100%",
      maxHeight: "100px",
      height: "100px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden"
    }}
  >
    {children}
  </Box>
);

const SilhouetteImage = ({
  silhouette,
  vehicleType,
  bikeTypeId,
  altText,
  style
}: SilhouetteImageProps) => (
  <ImageContainer>
    <Box
      component="img"
      src={mediaPerspective({
        perspective: silhouette,
        vehicleType: vehicleType,
        bikeTypeId: bikeTypeId
      })}
      alt={altText}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "contain"
      }}
      style={style}
    />
  </ImageContainer>
);

const SILHOUETTE_CONFIG = {
  damage: "Placeholder for damage view",
  frontAngle: "Placeholder for front angle view",
  front: "Placeholder for front view",
  backAngle: "Placeholder for back angle view",
  back: "Placeholder for back view",
  sideRight: "Placeholder for right side view",
  sideLeft: "Placeholder for left side view",
  frontTire: "Placeholder for front tire view",
  backTire: "Placeholder for back tire view",
  rightTrack: "Placeholder for right track view",
  leftTrack: "Placeholder for left track view",
  dashboard: "Placeholder for dashboard view",
  frontSeat: "Placeholder for front seat view",
  inDoor: "Placeholder for in door view",
  tire: "Placeholder for tire view",
  trunk: "Placeholder for trunk view",
  maintenanceBook: "Placeholder for maintenance book view",
  chainAndSprocket: "Placeholder for chain and sprocket view",
  // Caravan interior views
  interiorFromEntrance: "Placeholder for interior from entrance",
  towardsCabin: "Placeholder for view towards cabin",
  fromDriverArea: "Placeholder for view from driver area",
  sofaGroup: "Placeholder for sofa group",
  fromKitchen: "Placeholder for view from kitchen",
  toiletShower: "Placeholder for toilet and shower",
  bunkBeds: "Placeholder for bunk beds",
  moistMeasurement: "Placeholder for moisture measurement"
} as const;

const VehicleSilhouette = ({
  silhouette,
  vehicleType,
  bikeTypeId
}: VehicleSilhouetteProps) => {
  const altText =
    SILHOUETTE_CONFIG[silhouette as keyof typeof SILHOUETTE_CONFIG];

  if (!altText) {
    return null;
  }

  // Special case for sideLeft which needs horizontal flip (except for caravan)
  if (silhouette === "sideLeft" && vehicleType !== VehicleType.CARAVAN) {
    return (
      <SilhouetteImage
        silhouette={silhouette}
        vehicleType={vehicleType}
        bikeTypeId={bikeTypeId}
        altText={altText}
        style={{ transform: "scaleX(-1)" }}
      />
    );
  }

  return (
    <SilhouetteImage
      silhouette={silhouette}
      vehicleType={vehicleType}
      bikeTypeId={bikeTypeId}
      altText={altText}
    />
  );
};

export default VehicleSilhouette;
